import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { NumberField, SectionCard, parseNumberInput } from '../ui';
import type { LightspeedSettingsSectionProps } from './lightspeedSettingsRegistry';

const T = 'integrations.lightspeedConfig.photos';

/** What a push sends with no setting, and the most it ever sends (the API refuses more). */
export const LIGHTSPEED_DEFAULT_PHOTOS = 3;
export const LIGHTSPEED_MAX_PHOTOS = 10;

/**
 * How many photos Motzu sends per product to Lightspeed (flag `lightspeed_photo_limit`), and the most
 * it can send, so a store is not left wondering why an item has fewer photos than the catalog. Link-only
 * integrations and stores with Images off write no photos, so the section hides.
 */
const LightspeedPhotoLimitSection: React.FC<LightspeedSettingsSectionProps> = ({ settings, onChange }) => {
  const { t } = useAppTranslation();
  const saved = settings.maxPhotos ?? LIGHTSPEED_DEFAULT_PHOTOS;
  const [text, setText] = useState(String(saved));
  useEffect(() => setText(String(saved)), [saved]);
  // Flag `lightspeed_photo_topup`: an item with fewer photos than this gets Motzu's set on its next sync.
  const photoTopUp = useOrgFeatureFlag('lightspeed_photo_topup');

  if (settings.syncMode === 'link_only' || settings.syncImages === false) return null;

  const commit = (value: string) => {
    setText(value);
    const n = parseNumberInput(value);
    // Only a whole number in range is a change; anything else leaves the saved value (and the save bar) alone.
    if (n !== null && Number.isInteger(n) && n >= 1 && n <= LIGHTSPEED_MAX_PHOTOS && n !== saved) onChange({ maxPhotos: n });
  };

  return (
    <SectionCard title={t(`${T}.section`)} dense>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2">{t(`${T}.supports`, { max: LIGHTSPEED_MAX_PHOTOS })}</Typography>
        <NumberField
          integer
          min={1}
          max={LIGHTSPEED_MAX_PHOTOS}
          value={text}
          onChange={commit}
          label={t(`${T}.label`)}
          adornment={t(`${T}.unit`)}
          allowEmpty={false}
          size="small"
          sx={{ maxWidth: 220 }}
        />
        <Typography variant="caption" color="text.secondary">
          {t(`${T}.help`, { count: saved, max: LIGHTSPEED_MAX_PHOTOS })}
        </Typography>
        {photoTopUp.enabled && (
          <Typography variant="caption" color="text.secondary">
            {t(`${T}.topUp`)}
          </Typography>
        )}
      </Box>
    </SectionCard>
  );
};

export default LightspeedPhotoLimitSection;
